import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Drawer, Typography, IconButton, Button } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import axios from "axios";
import { API } from "../../utils/Api";
import Images from "../../utils/Images";
import { Player } from "@lottiefiles/react-lottie-player";
import CountdownRing from "../Helpers/CountdownRing";

const JourneyCouponDrawer = ({ open = true, onClose, onCancelOrder, cancelLoading, order }) => {
    const token = localStorage.getItem("token");
    const [journeyCoupon, setJourneyCoupon] = useState(null);
    const [userData, setUserData] = useState(JSON.parse(localStorage.getItem('userDataLogin')));

    const canvasRef = useRef(null);
    const wrapperRef = useRef(null);

    const [isDrawing, setIsDrawing] = useState(false);
    const [isRevealed, setIsRevealed] = useState(false);
    const revealedRef = useRef(false);
    const confettiRef = useRef(null);
    const [showConfetti, setShowConfetti] = useState(false);
    const [pop, setPop] = useState(false);
    const glowRef = useRef(null);

    const SCRATCH_RADIUS = 15;
    const AUTO_REVEAL_AT = 0.2;

    const CANCEL_WINDOW_SECONDS = 30;

    const cancelKey = React.useMemo(() => {
        const id = order?.order?._id;
        return id ? `journey_cancel_start_${id}` : null;
    }, [order?.order?._id]);

    const [remainingSeconds, setRemainingSeconds] = useState(CANCEL_WINDOW_SECONDS);
    const [canCancel, setCanCancel] = useState(false);

    const getUserJourneyCoupon = async () => {
        try {
            const response = await axios.get(
                `${API.BASE_URL}user-coupon-allocation/getAutoAllocatedCouponData`,
                {
                    params: { userId: userData?._id, orderId: order?.order?._id },
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setJourneyCoupon(response.data);
        } catch (error) {
            console.error("Failed to fetch journey coupon:", error);
        }
    };

    const initScratchCanvas = useCallback(async () => {
        if (revealedRef.current) return;
        const canvas = canvasRef.current;
        const wrapper = wrapperRef.current;
        if (!canvas || !wrapper) return;

        const imgEl = wrapper.querySelector("img[data-role='coupon']");
        if (!imgEl) return;

        const w = imgEl.clientWidth;
        const h = imgEl.clientHeight;
        if (!w || !h) return;

        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.globalCompositeOperation = "source-over";
        ctx.clearRect(0, 0, w, h);

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);

        const overlay = new Image();
        overlay.crossOrigin = "anonymous";
        overlay.src = Images.scratchCard;

        await new Promise((resolve, reject) => {
            overlay.onload = resolve;
            overlay.onerror = reject;
        });

        const imgRatio = overlay.width / overlay.height;
        const canvasRatio = w / h;

        let drawW, drawH, offsetX, offsetY;
        if (imgRatio > canvasRatio) {
            drawH = h;
            drawW = h * imgRatio;
            offsetX = (w - drawW) / 2;
            offsetY = 0;
        } else {
            drawW = w;
            drawH = w / imgRatio;
            offsetX = 0;
            offsetY = (h - drawH) / 2;
        }

        ctx.drawImage(overlay, offsetX, offsetY, drawW, drawH);
    }, []);

    const getPointerPos = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();

        if (e.touches && e.touches[0]) {
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top,
            };
        }

        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const scratchAt = (x, y) => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!canvas || !ctx) return;

        ctx.globalCompositeOperation = "destination-out";
        ctx.beginPath();
        ctx.arc(x, y, SCRATCH_RADIUS, 0, Math.PI * 2, false);
        ctx.fill();
    };

    const calcScratchedPercent = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!canvas || !ctx) return 0;

        const { width, height } = canvas;
        const data = ctx.getImageData(0, 0, width, height).data;

        let transparent = 0;
        const total = width * height;

        for (let i = 3; i < data.length; i += 4) {
            if (data[i] === 0) transparent++;
        }

        return transparent / total;
    };

    const revealAll = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!canvas || !ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        revealedRef.current = true;
        setIsRevealed(true);
        // stop glow permanently after reveal
        if (glowRef.current) {
            glowRef.current.stop();
        }

        // pop animation
        setPop(false);
        requestAnimationFrame(() => {
            setPop(true);
            setTimeout(() => setPop(false), 450);
        });

        // confetti blast (sync with pop)
        setShowConfetti(false);
        requestAnimationFrame(() => {
            setShowConfetti(true);
            // restart + play lottie
            if (confettiRef.current) {
                confettiRef.current.stop();
                confettiRef.current.play();
            }
            // hide after animation (tune duration to your lottie length)
            setTimeout(() => setShowConfetti(false), 1800);
        });

    };

    const handleStart = (e) => {
        if (revealedRef.current) return;
        setIsDrawing(true);
        const { x, y } = getPointerPos(e);
        scratchAt(x, y);
    };

    const handleMove = (e) => {
        if (!isDrawing || revealedRef.current) return;
        e.preventDefault();
        const { x, y } = getPointerPos(e);
        scratchAt(x, y);
    };

    const handleEnd = () => {
        if (revealedRef.current) return;
        setIsDrawing(false);

        const pct = calcScratchedPercent();
        if (pct >= AUTO_REVEAL_AT) revealAll();
    };

    useEffect(() => {
        if (open) getUserJourneyCoupon();
        console.log('Order sdfvasdfvsdfg', order?.order?._id);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const couponImg = journeyCoupon?.data?.couponData?.displayImage || Images.freebieCoupon;

    useEffect(() => {
        if (!open) return;

        revealedRef.current = false;
        setIsRevealed(false);
        setPop(false);

        const t = setTimeout(() => {
            initScratchCanvas();
        }, 50);

        const onResize = () => {
            if (!revealedRef.current) initScratchCanvas();
        };

        window.addEventListener("resize", onResize);

        return () => {
            clearTimeout(t);
            window.removeEventListener("resize", onResize);
        };
    }, [open, couponImg, initScratchCanvas]);

    useEffect(() => {
        if (!open) return;
        if (!cancelKey) return;

        // If start time not set, set it ONCE for this order
        let start = localStorage.getItem(cancelKey);
        if (!start) {
            localStorage.setItem(cancelKey, String(Date.now()));
            start = String(Date.now());
        }

        const startMs = Number(start);
        const elapsed = Math.floor((Date.now() - startMs) / 1000);
        const rem = CANCEL_WINDOW_SECONDS - elapsed;

        if (rem <= 0) {
            setRemainingSeconds(0);
            setCanCancel(false);
        } else {
            setRemainingSeconds(rem);
            setCanCancel(true);
        }
    }, [open, cancelKey]);

    useEffect(() => {
        if (!open) return;
        if (!cancelKey) return;

        const interval = setInterval(() => {
            const start = Number(localStorage.getItem(cancelKey) || Date.now());
            const elapsed = Math.floor((Date.now() - start) / 1000);
            const rem = CANCEL_WINDOW_SECONDS - elapsed;

            if (rem <= 0) {
                setRemainingSeconds(0);
                setCanCancel(false);
                clearInterval(interval);
            } else {
                setRemainingSeconds(rem);
                setCanCancel(true);
            }
        }, 250);

        return () => clearInterval(interval);
    }, [open, cancelKey]);

    const handleCountdownComplete = useCallback(() => {
        setCanCancel(false);
    }, []);

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            ModalProps={{
                keepMounted: true,
                BackdropProps: { sx: { backgroundColor: "rgba(0, 0, 0, 0.60)" } },
            }}
            PaperProps={{
                sx: {
                    height: "100vh",
                    background: "transparent",
                    boxShadow: "none",
                    overflow: "visible",
                },
            }}
            sx={{ zIndex: 2000 }}
        >
            <Box sx={{ position: "relative", height: "100vh" }}>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        zIndex: 10,
                        background: "rgba(255,255,255,0.9)",
                    }}
                >
                    <CloseRounded />
                </IconButton>

                <Box
                    sx={{
                        position: "absolute",
                        top: "45%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 5,
                        maxWidth: "95vw",
                        maxHeight: canCancel ? "100vh" : "100vh",
                    }}
                >
                    <Box
                        ref={wrapperRef}
                        sx={{
                            position: "relative",
                            display: "inline-block",
                            borderRadius: 3,
                            overflow: "hidden",
                            transformOrigin: "center",
                            animation: pop ? "couponPop 450ms ease-out" : "none",
                            "@keyframes couponPop": {
                                "0%": { transform: "scale(0.90)" },
                                "55%": { transform: "scale(1.26)" },
                                "80%": { transform: "scale(0.995)" },
                                "100%": { transform: "scale(1)" },
                            },
                        }}
                    >
                        <img
                            data-role="coupon"
                            src={couponImg}
                            alt="coupon"
                            draggable={false}
                            onLoad={() => {
                                if (!revealedRef.current) initScratchCanvas();
                            }}
                            style={{
                                display: "block",
                                maxWidth: "95vw",
                                maxHeight: "70vh",
                                width: "50vw",
                                height: "40vh",
                                objectFit: "contain",
                                userSelect: "none",
                                pointerEvents: "none",
                            }}
                        />
                        <canvas
                            ref={canvasRef}
                            style={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                cursor: isRevealed ? "default" : "crosshair",
                                touchAction: "none",
                                pointerEvents: isRevealed ? "none" : "auto",
                            }}
                            onMouseDown={handleStart}
                            onMouseMove={handleMove}
                            onMouseUp={handleEnd}
                            onMouseLeave={handleEnd}
                            onTouchStart={handleStart}
                            onTouchMove={handleMove}
                            onTouchEnd={handleEnd}
                        />
                    </Box>
                </Box>
                {!isRevealed && !showConfetti && (
                    <Player
                        ref={confettiRef}
                        autoplay
                        loop={true}
                        keepLastFrame={false}
                        src={Images.glowingBg}
                        style={{ position: "absolute", transform: "translate(0%, -15%)", }}
                    />
                )}

                {showConfetti && (
                    <Player
                        ref={confettiRef}
                        autoplay
                        loop={false}
                        keepLastFrame={false}
                        src={Images.conffetti}
                        style={{ width: "100%", height: "100%", }}
                    />
                )}


                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "#fff",
                        borderTopLeftRadius: 18,
                        borderTopRightRadius: 18,
                        p: 2,
                        boxShadow: "0 -10px 30px rgba(0,0,0,0.15)",
                        zIndex: 6,
                        pb: canCancel ? 2 : 8,
                    }}
                >
                    <Typography sx={{ fontWeight: 900, fontSize: 18 }}>
                        Scratch Card Earned!
                    </Typography>

                    <Typography sx={{ fontSize: 15, color: "#7A7A7A", mt: 0.5 }}>
                        Scratch the card above and you could earn<br /> rewards!
                    </Typography>

                    {canCancel && (
                        <Box sx={{ mt: 2, display: "flex", alignItems: 'center', gap: 1, fontSize: 15 }}>
                            <Typography color={'red'}>Preparing your order in </Typography>

                            <CountdownRing seconds={remainingSeconds} size={36} stroke={2} onComplete={() => setCanCancel(false)} />
                            <Button disabled={cancelLoading} onClick={() => {
                                onCancelOrder?.();
                                onClose?.();
                            }}
                                sx={{ border: !cancelLoading ? '1px solid red' : '1px solid #c6c6c6', color: !cancelLoading ? 'red' : '#c6c6c6', borderRadius: '30px', fontSize: 10, px: 2 }}
                            >
                                {cancelLoading ? "Cancelling..." : "Cancel order"}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

        </Drawer>
    );
};

export default JourneyCouponDrawer;